import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../state/root';
import { selectUser } from '../../state/user/user.selectors';
import { ColourMode, StylingService } from '../../services/styling/styling.service';
import { UserActions } from '../../state/user/user.actions';
import { MatChip, MatChipAvatar, MatChipSet } from '@angular/material/chips';
import { MatDivider } from '@angular/material/divider';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { environment } from '../../../../environments/environment';
import { SystemAlertsService } from '../../services/system-alerts/system-alerts.service';
import { Subscription } from 'rxjs';
import { ExperimentalWrapperComponent } from '../experimental-wrapper/experimental-wrapper.component';
import { AlertLockActions } from '../../state/alert/alert.actions';
import { selectLocksToRelease, selectWindow } from '../../state/alert/alert.selectors';
import { KeyboardService } from '../../services/keyboard/keyboard.service';
import { SettingsActions } from '../../state/settings/settings.actions';
import { selectExperimentalMode } from '../../state/settings/settings.selectors';

const MAT_IMPORTS = [MatChip, MatChipAvatar, MatChipSet, MatDivider, MatMenu, MatMenuItem, MatMenuTrigger, MatToolbar, MatIcon, MatIconButton];

@Component({
  selector: 'lh-top-nav',
  standalone: true,
  imports: [...MAT_IMPORTS, ExperimentalWrapperComponent],
  templateUrl: './top-nav.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./top-nav.component.scss'],
})
export class TopNavComponent implements OnInit, OnDestroy {
  private store = inject(Store<AppState>);
  private stylingService = inject(StylingService);
  private systemAlertsService = inject(SystemAlertsService);

  public user = this.store.selectSignal(selectUser);
  public buildVersion = (window as Window & { appVersion?: string }).appVersion || 'Local Build';
  public colourMode = ColourMode.LIGHT;
  public hasNotifications = false;
  private notificationSub: Subscription | undefined;
  private window = this.store.selectSignal(selectWindow);
  private locksToRelease = this.store.selectSignal(selectLocksToRelease);
  private keyboardService = inject(KeyboardService);
  private experimentalMode = this.store.selectSignal(selectExperimentalMode);

  platform = environment.platform

  constructor() {
    this.keyboardService.subscribe("top-nav", (event, grabbed) => {
      const combo = this.keyboardService.combination(event);
      if (this.keyboardService.combination("KeyE", { altKey: true, shiftKey: true, ctrlKey: true }) === combo && !grabbed) {
        this.store.dispatch(SettingsActions.experimentalMode({ enable: !this.experimentalMode() }));
      }
    });
  }

  ngOnInit(): void {
    this.stylingService.colourMode$.subscribe(e => {
      this.colourMode = e
    })

    this.notificationSub = this.systemAlertsService.notifications.subscribe(() => {
      this.hasNotifications = true;
    });
  }

  ngOnDestroy(): void {
    this.notificationSub?.unsubscribe();
    this.keyboardService.removeSubscriptions("top-nav");
  }

  toggleColourMode(newMode: ColourMode) {
    this.stylingService.setColourMode(newMode)
  }

  menuClicked() {
    this.stylingService.toggleSideNav(true)
  }

  private alertIdsToRelease() {
    return [...new Set([...this.window().alerts, ...this.locksToRelease()])];
  }

  logout() {
    this.store.dispatch(AlertLockActions.releaseLocks({ ids: this.alertIdsToRelease() }));
    this.store.dispatch(UserActions.logout());
  }

  openAlerts() {
    this.stylingService.toggleAlertPanel(true)
    this.hasNotifications = false;
  }
}
